import { useCallback, useMemo, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Button, LoadingState, EditableEntityCard, Input } from '@/components';
import { DataList, Panel } from '@/components/blocks/data-display';
import { useBreadcrumbEffect, type BreadcrumbItem } from '@/components/blocks/chrome';
import { EmptyState } from '@/components/blocks/feedback';
import {
  PageHeader,
  TabNavigation,
  SectionHeader,
  PageLayout,
  PageContent,
} from '@/components/blocks/page';
import { Section } from '@/components/blocks/layout';
import {
  EntityDialog,
  EntityDialogFooter,
  FormField,
  FormGrid,
  FormSection,
} from '@/components/blocks/forms';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  CatalogSuggestTextSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { useCompany, useDeleteRefrigerationUnit, useUpdateRefrigerationUnit } from '../../hooks';
import { useEntityDevLogger } from '@/hooks/useEntityDevLogger';
import { useDialogState } from '@/hooks/useDialogState';
import type { RefrigerationUnit } from '@/data/schemas';
import { CATALOG_IDS } from '@/data/models/catalog';
import { catalogService } from '@/data/services/catalogService';
import { queryKeys } from '@/lib/query-keys';
import { newId } from '../../../../utils/ids';
import { RefrigerationUnitModal } from './RefrigerationUnitModal';
import { RefrigerationUnitSafetySectionModal } from './RefrigerationUnitSafetySectionModal';
import { SafetySubTabForm } from './components/SafetySubTabForm';
import { DetectorPositionsField } from './sections/DetectorPositionsField';
import { buildSafetyDefaults, buildSafetyValues, safetyToInput } from './config/safetyFields';
import {
  buildVentilationDefaults,
  buildVentilationValues,
  ventilationToInput,
} from './config/ventilationFields';
import {
  buildAbbattimentoCombinedDefaults,
  buildAbbattimentoCombinedValues,
  abbattimentoCombinedToInput,
} from './config/abbattimentoCombinedFields';
import {
  buildEmergenzaCombinedDefaults,
  buildEmergenzaCombinedValues,
  emergenzaCombinedToInput,
} from './config/emergenzaCombinedFields';
import { SafetyChainSummary } from './components/SafetyChainSummary';
import { SafetySubTabView } from './components/SafetySubTabView';
import { RefrigerationUnitAnagrafica } from './RefrigerationUnitAnagrafica';
import {
  rilevazioneFields,
  rilevazioneSections,
  rilevazioneScalarFields,
  rilevazioneFormSections,
  ventilazioneFields,
  ventilazioneSections,
  ventilazioneFormSections,
  abbattimentoFields,
  abbattimentoSections,
  abbattimentoFormSections,
  emergenzaFields,
  emergenzaSections,
  emergenzaFormSections,
} from './config/schemas';

const allowedTabs = ['operativita', 'componenti', 'sicurezza', 'anagrafica'] as const;
type UnitTab = (typeof allowedTabs)[number];

const allowedSafetyTabs = ['rilevazione', 'ventilazione', 'abbattimento', 'emergenza'] as const;
type SafetyTab = (typeof allowedSafetyTabs)[number];

// Safety subtab labels (renamed per plan)
const SAFETY_TAB_LABELS: Record<SafetyTab, string> = {
  rilevazione: 'Rilevazione & Allarmi',
  ventilazione: 'Ventilazione & Estrazione',
  abbattimento: 'Abbattimento & Scarico',
  emergenza: 'Emergenza & Continuità',
};

type ComponentModalState = {
  mode: 'create' | 'edit';
  component: RefrigerationUnit['technical']['components'][number] | null;
};

type ComponentType = RefrigerationUnit['technical']['components'][number]['type'];
const CUSTOM_COMPONENT_TYPE = '__custom__';

function formatBoolean(value: boolean | undefined | null): string {
  if (value === undefined || value === null) return '—';
  return value ? 'Sì' : 'No';
}

function normalizeText(value: string | undefined | null): string {
  return (value ?? '').trim();
}

function formatEnumValue(
  value: string | undefined | null,
  labels?: Record<string, string>
): string {
  if (!value) return '—';
  const label = labels?.[value] ?? value.replace(/_/g, ' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function getComponentLabel(
  component: RefrigerationUnit['technical']['components'][number],
  labelMap?: Map<string, string>
): string {
  if (component.customLabel) {
    return component.customLabel;
  }
  if (!component.type) {
    return 'Componente';
  }
  return labelMap?.get(component.type) ?? component.type;
}

function ComponentModal({
  state,
  onOpenChange,
  onSave,
  onDelete,
  isSubmitting,
  isDeleting,
  componentOptions,
}: {
  state: ComponentModalState | null;
  onOpenChange: (open: boolean) => void;
  onSave: (input: {
    id?: string;
    type?: ComponentType;
    customLabel?: string;
    quantity?: number;
    capacityKw?: number;
    location?: string;
    notes?: string;
    btGreaseBand?: boolean;
  }) => Promise<void>;
  onDelete?: () => Promise<void>;
  isSubmitting: boolean;
  isDeleting: boolean;
  componentOptions: Array<{ value: string; label: string }>;
}): JSX.Element {
  const open = Boolean(state);
  const mode = state?.mode ?? 'create';
  const component = state?.component ?? null;

  const optionValues = useMemo(
    () => componentOptions.map((option) => option.value),
    [componentOptions]
  );
  const resolveInitialType = useCallback(
    (value: string | undefined) => {
      if (!value) return CUSTOM_COMPONENT_TYPE;
      return optionValues.includes(value) ? value : CUSTOM_COMPONENT_TYPE;
    },
    [optionValues]
  );
  const initialCustomLabel = useMemo(() => {
    if (!component) return '';
    if (optionValues.includes(component.type ?? '')) {
      return component.customLabel ?? '';
    }
    return component.customLabel ?? component.type ?? '';
  }, [component, optionValues]);

  const defaultType = optionValues[0] ?? CUSTOM_COMPONENT_TYPE;
  const initialType = component?.type
    ? resolveInitialType(component.type)
    : initialCustomLabel
      ? CUSTOM_COMPONENT_TYPE
      : defaultType;

  const [type, setType] = useState<ComponentType>(initialType);
  const [quantity, setQuantity] = useState<number | null>(component?.quantity ?? null);
  const [capacityKw, setCapacityKw] = useState<number | null>(component?.capacityKw ?? null);
  const [customLabel, setCustomLabel] = useState(initialCustomLabel);
  const [location, setLocation] = useState(component?.location ?? '');
  const [notes, setNotes] = useState(component?.notes ?? '');
  const [btGreaseBand, setBtGreaseBand] = useState(component?.btGreaseBand ?? false);

  const resetState = useCallback(() => {
    setType(initialType);
    setQuantity(component?.quantity ?? null);
    setCapacityKw(component?.capacityKw ?? null);
    setCustomLabel(initialCustomLabel);
    setLocation(component?.location ?? '');
    setNotes(component?.notes ?? '');
    setBtGreaseBand(component?.btGreaseBand ?? false);
  }, [component, initialCustomLabel, initialType]);

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      resetState();
    }
    onOpenChange(nextOpen);
  };

  const initialSnapshot = useMemo(
    () => ({
      type: initialType,
      quantity: component?.quantity ?? null,
      capacityKw: component?.capacityKw ?? null,
      customLabel: initialCustomLabel,
      location: component?.location ?? '',
      notes: component?.notes ?? '',
      btGreaseBand: component?.btGreaseBand ?? false,
    }),
    [component, initialCustomLabel, initialType]
  );

  const isDirty =
    type !== initialSnapshot.type ||
    quantity !== initialSnapshot.quantity ||
    capacityKw !== initialSnapshot.capacityKw ||
    customLabel !== initialSnapshot.customLabel ||
    location !== initialSnapshot.location ||
    notes !== initialSnapshot.notes ||
    btGreaseBand !== initialSnapshot.btGreaseBand;

  const handleSubmit = async () => {
    const normalizedCapacity =
      capacityKw === null || Number.isNaN(capacityKw) ? undefined : capacityKw;
    const normalizedType = type === CUSTOM_COMPONENT_TYPE ? undefined : type;
    const trimmedCustomLabel = customLabel.trim();
    const normalizedCustomLabel = trimmedCustomLabel || undefined;
    const normalizedQuantity = quantity === null || Number.isNaN(quantity) ? undefined : quantity;
    if (!normalizedType && !normalizedCustomLabel) {
      return;
    }
    const payload = {
      id: component?.id,
      type: normalizedType,
      customLabel: normalizedType === undefined ? normalizedCustomLabel : undefined,
      quantity: normalizedQuantity,
      capacityKw: normalizedCapacity,
      location: location || undefined,
      notes: notes || undefined,
      btGreaseBand: btGreaseBand || undefined,
    };
    await onSave(payload);
  };

  return (
    <EntityDialog
      open={open}
      onOpenChange={handleDialogOpenChange}
      title={mode === 'create' ? 'Nuovo componente' : 'Modifica componente'}
      size="sm"
      isDirty={isDirty}
      isSaving={isSubmitting || isDeleting}
      footer={
        <EntityDialogFooter
          extra={
            mode === 'edit' && onDelete ? (
              <Button
                type="button"
                variant="danger"
                onClick={onDelete}
                disabled={isDeleting || isSubmitting}
              >
                Rimuovi componente
              </Button>
            ) : null
          }
          secondary={
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleDialogOpenChange(false)}
              disabled={isSubmitting || isDeleting}
            >
              Chiudi
            </Button>
          }
          primary={
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || (type === CUSTOM_COMPONENT_TYPE && !customLabel.trim())}
            >
              {isSubmitting ? 'Salvataggio…' : 'Salva'}
            </Button>
          }
        />
      }
    >
      <div className="space-y-4">
        <FormSection title="Informazioni componente">
          <FormGrid>
            <FormField label="Tipologia" htmlFor="component-type" colSpan={2}>
              <Select
                value={type}
                onValueChange={(value) => {
                  setType(value);
                  if (value !== CUSTOM_COMPONENT_TYPE) {
                    setCustomLabel('');
                  }
                }}
              >
                <SelectTrigger id="component-type">
                  <SelectValue placeholder="Seleziona tipologia" />
                </SelectTrigger>
                <SelectContent>
                  {componentOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                  <SelectItem value={CUSTOM_COMPONENT_TYPE}>Altro (personalizzato)</SelectItem>
                </SelectContent>
              </Select>
            </FormField>

            {type === CUSTOM_COMPONENT_TYPE ? (
              <FormField label="Descrizione" htmlFor="component-custom-label" colSpan={2}>
                <Input
                  id="component-custom-label"
                  value={customLabel}
                  onChange={(event) => setCustomLabel(event.target.value)}
                  placeholder="Es: Compressore alternativo"
                />
              </FormField>
            ) : null}
          </FormGrid>
        </FormSection>

        <FormSection title="Parametri">
          <FormGrid>
            <FormField label="Quantità" htmlFor="component-quantity">
              <Input
                id="component-quantity"
                type="number"
                min={0}
                step={1}
                value={quantity ?? ''}
                onChange={(event) =>
                  setQuantity(event.target.value === '' ? null : Number(event.target.value))
                }
                placeholder="Es: 2"
              />
            </FormField>
            <FormField label="Potenza/Capacità (kW)" htmlFor="component-capacity">
              <Input
                id="component-capacity"
                type="number"
                min={0}
                step={0.1}
                value={capacityKw ?? ''}
                onChange={(event) => {
                  const next = event.target.value;
                  setCapacityKw(next ? Number(next) : null);
                }}
                placeholder="Es: 45"
              />
            </FormField>
            <FormField label="Ubicazione" htmlFor="component-location">
              <CatalogSuggestTextSelect
                id="component-location"
                catalogId={CATALOG_IDS.PLANT_LOCATIONS}
                value={location}
                onValueChange={(value) => setLocation(value ?? '')}
                placeholder="Seleziona localizzazione"
                createIdPrefix="ubicazione"
              />
            </FormField>
          </FormGrid>
        </FormSection>

        <FormSection title="Protezioni">
          <FormField label="" htmlFor="component-bt-grease-band" colSpan={2}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                id="component-bt-grease-band"
                checked={btGreaseBand}
                onChange={(event) => setBtGreaseBand(event.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm">Benda grassa BT presente</span>
            </label>
          </FormField>
        </FormSection>

        <FormSection title="Note">
          <FormField label="Note" htmlFor="component-notes" colSpan={2}>
            <Textarea
              id="component-notes"
              className="min-h-[90px]"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Note facoltative"
            />
          </FormField>
        </FormSection>
      </div>
    </EntityDialog>
  );
}

export function RefrigerationUnitDetailScreen(): JSX.Element {
  const params = useParams({
    from: '/companies/$companyId/sites/$siteId/units/$unitId',
  });
  const navigate = useNavigate({
    from: '/companies/$companyId/sites/$siteId/units/$unitId',
  });
  const search = useSearch({
    from: '/companies/$companyId/sites/$siteId/units/$unitId',
  });
  const { data: company, isLoading } = useCompany(params.companyId);
  const site = useMemo(() => {
    if (!company) return null;
    return company.sites.find((item) => item.id === params.siteId) ?? null;
  }, [company, params.siteId]);

  const { data: componentCategoryOptions = [] } = useQuery({
    queryKey: queryKeys.componentCategories.all,
    queryFn: () => catalogService.getCatalogOptions(CATALOG_IDS.COMPONENT_CATEGORIES),
  });
  const { data: plantLocationOptions = [] } = useQuery({
    queryKey: queryKeys.catalogs.options(CATALOG_IDS.PLANT_LOCATIONS),
    queryFn: () =>
      catalogService.getCatalogOptions(CATALOG_IDS.PLANT_LOCATIONS, {
        includeInactive: true,
      }),
  });
  const { data: plantTypeOptions = [] } = useQuery({
    queryKey: queryKeys.catalogs.options(CATALOG_IDS.PLANT_TYPES),
    queryFn: () =>
      catalogService.getCatalogOptions(CATALOG_IDS.PLANT_TYPES, {
        includeInactive: true,
      }),
  });
  const { data: toxicGasOptions = [] } = useQuery({
    queryKey: queryKeys.catalogs.options(CATALOG_IDS.TOXIC_GASES),
    queryFn: () =>
      catalogService.getCatalogOptions(CATALOG_IDS.TOXIC_GASES, {
        includeInactive: true,
      }),
  });
  const { data: thresholdActionOptions = [] } = useQuery({
    queryKey: queryKeys.catalogs.options(CATALOG_IDS.THRESHOLD_ACTIONS),
    queryFn: () =>
      catalogService.getCatalogOptions(CATALOG_IDS.THRESHOLD_ACTIONS, {
        includeInactive: true,
      }),
  });
  const componentLabelMap = useMemo(
    () => new Map(componentCategoryOptions.map((option) => [option.id, option.label])),
    [componentCategoryOptions]
  );
  const thresholdActionLabelMap = useMemo(
    () => new Map(thresholdActionOptions.map((option) => [option.id, option.label])),
    [thresholdActionOptions]
  );
  const plantLocationLabelMap = useMemo(
    () => new Map(plantLocationOptions.map((option) => [option.id, option.label])),
    [plantLocationOptions]
  );

  const unit = useMemo(() => {
    if (!site) return null;
    return site.refrigerationUnits?.find((item) => item.id === params.unitId) ?? null;
  }, [params.unitId, site]);

  const devPayload = useMemo(() => {
    if (!company || !site || !unit) return null;
    return {
      company,
      site,
      unit: unit,
    };
  }, [company, site, unit]);

  useEntityDevLogger('Refrigeration unit detail', devPayload);

  const updateRefrigerationUnit = useUpdateRefrigerationUnit(params.companyId, params.siteId);
  const deleteRefrigerationUnit = useDeleteRefrigerationUnit(params.companyId, params.siteId);

  const rawTab = typeof search.tab === 'string' ? search.tab : undefined;
  const currentTab: UnitTab =
    rawTab && allowedTabs.includes(rawTab as UnitTab)
      ? (rawTab as UnitTab)
      : rawTab === 'overview' || rawTab === 'anagrafica'
        ? 'anagrafica'
        : 'operativita';
  const rawSafetyTab = typeof search.safety === 'string' ? search.safety : undefined;
  const currentSafetyTab: SafetyTab =
    rawSafetyTab && allowedSafetyTabs.includes(rawSafetyTab as SafetyTab)
      ? (rawSafetyTab as SafetyTab)
      : 'rilevazione';

  const breadcrumbFactory = useCallback(
    (baseTrail: BreadcrumbItem[]) => {
      const defaultTrail = [...baseTrail];

      if (!company || !site) {
        return [...defaultTrail, { label: 'Impianto' }];
      }

      const companyHref = `/companies/${company.id}`;
      const siteHref = `/companies/${company.id}/sites/${site.id}`;

      return [
        ...defaultTrail,
        { label: company.name, href: companyHref },
        { label: site.name, href: siteHref },
        { label: unit?.name ?? 'Impianto' },
      ];
    },
    [company, site, unit]
  );

  useBreadcrumbEffect(breadcrumbFactory);

  const [unitModalOpen, setUnitModalOpen] = useDialogState('unitDetailEdit');
  const [componentModal, setComponentModal] = useState<ComponentModalState | null>(null);
  const [detectionModalOpen, setDetectionModalOpen] = useDialogState('unitSafetyDetection');
  const [ventilationModalOpen, setVentilationModalOpen] = useDialogState('unitSafetyVentilation');
  const [abbattimentoModalOpen, setAbbattimentoModalOpen] =
    useDialogState('unitSafetyAbbattimento');
  const [emergenzaModalOpen, setEmergenzaModalOpen] = useDialogState('unitSafetyEmergenza');

  const handleTabSelect = useCallback(
    (tabId: UnitTab) => {
      navigate({
        to: '/companies/$companyId/sites/$siteId/units/$unitId',
        params: {
          companyId: params.companyId,
          siteId: params.siteId,
          unitId: params.unitId,
        },
        search: {
          ...search,
          tab: tabId === 'operativita' ? undefined : tabId,
        },
        replace: true,
      });
    },
    [navigate, params.companyId, params.siteId, params.unitId, search]
  );

  const handleSafetyTabSelect = useCallback(
    (tabId: SafetyTab) => {
      navigate({
        to: '/companies/$companyId/sites/$siteId/units/$unitId',
        params: {
          companyId: params.companyId,
          siteId: params.siteId,
          unitId: params.unitId,
        },
        search: {
          ...search,
          safety: tabId === 'rilevazione' ? undefined : tabId,
        },
        replace: true,
      });
    },
    [navigate, params.companyId, params.siteId, params.unitId, search]
  );

  const ventilation = unit?.technical?.safety?.ventilation;
  const extractors = ventilation?.extractors;
  const scrubber = unit?.technical?.safety?.scrubber;
  const abatementTank = unit?.technical?.safety?.abatementTank;
  const emergency = unit?.technical?.safety?.emergencySystems;
  const power = unit?.technical?.safety?.powerBackup;

  const scrubberPresent = scrubber?.present ?? false;
  const scrubberHasDetails =
    scrubberPresent ||
    Boolean(scrubber?.notes?.trim()) ||
    scrubber?.airFlowM3h !== undefined ||
    scrubber?.waterFlowM3h !== undefined ||
    Boolean(scrubber?.waterFlowSpec) ||
    scrubber?.contactTimeSeconds !== undefined ||
    Boolean(scrubber?.waterSupply) ||
    Boolean(scrubber?.wasteCollection) ||
    scrubber?.atex !== undefined;
  const scrubberPresentLabel = scrubberHasDetails ? formatBoolean(scrubberPresent) : '—';

  const abatementTankHasDetails =
    abatementTank?.volumeMc !== undefined ||
    abatementTank?.minWaterMc !== undefined ||
    abatementTank?.minFreeMc !== undefined ||
    Boolean(abatementTank?.location) ||
    Boolean(abatementTank?.signage) ||
    Boolean(abatementTank?.accessibility) ||
    Boolean(abatementTank?.material) ||
    Boolean(abatementTank?.dimensions) ||
    Boolean(abatementTank?.orientation) ||
    abatementTank?.levelInstrumentation?.present !== undefined ||
    Boolean(abatementTank?.levelInstrumentation?.type) ||
    abatementTank?.continuousPhMonitoring !== undefined ||
    abatementTank?.emergencySupply?.preferredLine !== undefined ||
    abatementTank?.emergencySupply?.generator !== undefined ||
    abatementTank?.emergencySupply?.ups !== undefined;
  const abatementTankPresent = abatementTank?.present ?? abatementTankHasDetails;
  const abatementTankPresentLabel = abatementTankHasDetails
    ? formatBoolean(abatementTankPresent)
    : '—';

  const emergencyHasDetails =
    emergency?.dialerPresent === true ||
    emergency?.guardServicePresent === true ||
    emergency?.generatorPresent === true ||
    emergency?.eeTripButtons !== undefined ||
    emergency?.eeTripDetector !== undefined ||
    emergency?.elettroserraturaPresent !== undefined ||
    emergency?.emergencyLightingPresent !== undefined ||
    emergency?.emergencyLightingAtex !== undefined ||
    emergency?.sgancioAutomaticoPresent !== undefined ||
    Boolean(emergency?.sogliaSgancioAutomatico) ||
    emergency?.pulsanteSgancioPresent !== undefined ||
    Boolean(emergency?.notes);

  const thresholdsCount = unit?.technical?.safety?.detectors?.thresholds?.length ?? 0;
  const detectionSummary = `${
    unit?.technical?.safety?.detectors?.toxicCount ?? 0
  } tossici, ${unit?.technical?.safety?.detectors?.explosiveCount ?? 0} esplosivo, ${thresholdsCount} soglie`;
  const ventilationTypeSummary = ventilation?.type
    ? formatEnumValue(ventilation?.type)
    : 'Non specificato';
  const ventilationSummary = `${ventilationTypeSummary}, ATEX ${formatBoolean(extractors?.atex)}`;
  const abatementSummary = `Scrubber ${scrubberPresentLabel}, Vasca ${abatementTankPresentLabel}`;
  const emergencySummary = emergencyHasDetails
    ? `Sgancio ${formatBoolean(
        emergency?.sgancioAutomaticoPresent
      )}, UPS ${formatBoolean(power?.ups)}`
    : '—';
  const safetyOverviewItems: Array<{
    id: SafetyTab;
    label: string;
    summary: string;
  }> = [
    {
      id: 'rilevazione',
      label: SAFETY_TAB_LABELS.rilevazione,
      summary: detectionSummary,
    },
    {
      id: 'ventilazione',
      label: SAFETY_TAB_LABELS.ventilazione,
      summary: ventilationSummary,
    },
    {
      id: 'abbattimento',
      label: SAFETY_TAB_LABELS.abbattimento,
      summary: abatementSummary,
    },
    {
      id: 'emergenza',
      label: SAFETY_TAB_LABELS.emergenza,
      summary: emergencySummary,
    },
  ];

  const handleUnitUpdate = useCallback(
    async (input: Parameters<typeof updateRefrigerationUnit.mutateAsync>[0]) => {
      try {
        await updateRefrigerationUnit.mutateAsync(input);
      } catch {
        toast.error("Impossibile aggiornare l'impianto");
        throw new Error('update failed');
      }
    },
    [updateRefrigerationUnit]
  );

  const handleComponentSave = async (payload: {
    id?: string;
    type?: ComponentType;
    customLabel?: string;
    quantity?: number;
    capacityKw?: number;
    location?: string;
    notes?: string;
    btGreaseBand?: boolean;
  }) => {
    if (!unit) return;
    const nextComponents =
      componentModal?.mode === 'edit' && componentModal.component
        ? (unit.technical?.components ?? []).map((component) =>
            component.id === componentModal.component?.id
              ? {
                  ...component,
                  type: payload.type,
                  customLabel: payload.customLabel,
                  quantity: payload.quantity,
                  capacityKw: payload.capacityKw,
                  location: payload.location,
                  notes: payload.notes,
                  btGreaseBand: payload.btGreaseBand,
                }
              : component
          )
        : [
            ...(unit.technical?.components ?? []),
            {
              id: payload.id ?? newId(),
              type: payload.type,
              customLabel: payload.customLabel,
              quantity: payload.quantity,
              capacityKw: payload.capacityKw,
              location: payload.location,
              notes: payload.notes,
              btGreaseBand: payload.btGreaseBand,
            },
          ];
    try {
      await handleUnitUpdate({
        id: unit.id,
        technical: {
          ...unit.technical,
          components: nextComponents,
        },
      });
      toast.success('Componente salvato');
      setComponentModal(null);
    } catch {
      /* error surfaced by handleUnitUpdate */
    }
  };

  const handleComponentDelete = async () => {
    if (!unit || !componentModal?.component) return;
    const nextComponents = (unit.technical?.components ?? []).filter(
      (component) => component.id !== componentModal.component?.id
    );
    try {
      await handleUnitUpdate({
        id: unit.id,
        technical: {
          ...unit.technical,
          components: nextComponents,
        },
      });
      toast.success('Componente rimosso');
      setComponentModal(null);
    } catch {
      /* handled above */
    }
  };

  const handleUnitDelete = async () => {
    if (!unit) return;
    await deleteRefrigerationUnit.mutateAsync(unit.id);
    await navigate({
      to: '/companies/$companyId/sites/$siteId',
      params: { companyId: params.companyId, siteId: params.siteId },
      search: { tab: undefined },
    });
  };

  /**
   * Handler for updating embedded array fields via schema-driven tables.
   * Parses the dataPath and constructs the appropriate update input.
   */
  const handleArrayUpdate = useCallback(
    async (dataPath: string, items: unknown[]) => {
      if (!unit) return;

      // Currently handles "technical.safety.detectors.thresholds"
      // Can be extended to handle other paths as needed
      if (dataPath === 'technical.safety.detectors.thresholds') {
        // Items from the EmbeddedArrayTable should have all required fields
        // Ensure all items have IDs
        type ThresholdItem = {
          id?: string;
          thresholdType?: 'tossicita' | 'esplosivita';
          unit: 'ppm' | 'percent_lel';
          value: number;
          actions: string[];
        };
        const thresholds = items.map((item) => {
          const threshold = item as ThresholdItem;
          return {
            ...threshold,
            id: threshold.id || newId(),
          };
        });

        await handleUnitUpdate({
          id: unit.id,
          technical: {
            safety: {
              detectors: { thresholds },
            },
          },
        });
        toast.success('Soglie aggiornate');
      } else {
        console.warn(`Unhandled array dataPath: ${dataPath}`);
      }
    },
    [unit, handleUnitUpdate]
  );

  const unitContextLine = useMemo(() => {
    if (!company || !site || !unit) return undefined;
    const code = unit.refrigerationUnitId || unit.name;
    return `${company.name} · ${site.name} · ${code}`;
  }, [company, site, unit]);

  if (isLoading) {
    return <LoadingState label="Caricamento impianto" />;
  }

  if (!company || !site || !unit) {
    return <></>;
  }

  const anagraficaTabContent = (
    <Section spacing="md">
      <SectionHeader
        title="Anagrafica impianto"
        action={{
          label: 'Modifica',
          onClick: () => setUnitModalOpen(true),
        }}
      />
      <RefrigerationUnitAnagrafica
        unit={unit}
        siteName={site.name}
        plantLocationOptions={plantLocationOptions}
        toxicGasOptions={toxicGasOptions}
        plantTypeOptions={plantTypeOptions}
      />
    </Section>
  );

  const componentiTabContent = (
    <Section spacing="md">
      <SectionHeader
        title="Componenti"
        action={{
          label: 'Nuovo componente',
          onClick: () => setComponentModal({ mode: 'create', component: null }),
        }}
      />
      {(unit.technical?.components ?? []).length === 0 ? (
        <Panel>
          <EmptyState
            icon="build"
            title="Nessun componente registrato"
            description="Nessun componente registrato per questo impianto."
            variant="inline"
          />
        </Panel>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {(unit.technical?.components ?? []).map((component) => (
            <EditableEntityCard
              key={component.id}
              title={getComponentLabel(component, componentLabelMap)}
              subtitle={component.location || undefined}
              onEdit={() => setComponentModal({ mode: 'edit', component })}
            >
              <DataList
                size="sm"
                items={[
                  {
                    label: 'Quantità',
                    value: component.quantity ?? '—',
                    mono: true,
                  },
                  {
                    label: 'Potenza/Capacità',
                    value:
                      component.capacityKw !== undefined && component.capacityKw !== null
                        ? `${component.capacityKw} kW`
                        : '—',
                    mono: true,
                  },
                  {
                    label: 'Ubicazione',
                    value: component.location || '—',
                  },
                  {
                    label: 'Benda grassa BT',
                    value: component.btGreaseBand ? 'Sì' : 'No',
                  },
                  {
                    label: 'Note',
                    value: component.notes || '—',
                    layout: 'stacked',
                    valueClassName: 'text-xs font-normal text-text-primary',
                  },
                ]}
              />
            </EditableEntityCard>
          ))}
        </div>
      )}
    </Section>
  );

  const rilevazioneTabContent = (
    <SafetySubTabView
      unit={unit}
      fields={rilevazioneFields}
      sections={rilevazioneSections}
      title="Sistemi di rilevazione"
      onEdit={() => setDetectionModalOpen(true)}
      onUpdateArray={handleArrayUpdate}
      isUpdating={updateRefrigerationUnit.isPending}
      catalogLabelMaps={{
        [CATALOG_IDS.THRESHOLD_ACTIONS]: thresholdActionLabelMap,
        [CATALOG_IDS.PLANT_LOCATIONS]: plantLocationLabelMap,
      }}
    />
  );

  const ventilazioneTabContent = (
    <SafetySubTabView
      unit={unit}
      fields={ventilazioneFields}
      sections={ventilazioneSections}
      title="Ventilazione"
      onEdit={() => setVentilationModalOpen(true)}
    />
  );

  const abbattimentoTabContent = (
    <SafetySubTabView
      unit={unit}
      fields={abbattimentoFields}
      sections={abbattimentoSections}
      title="Abbattimento & Scarico"
      onEdit={() => setAbbattimentoModalOpen(true)}
    />
  );

  const emergenzaTabContent = (
    <SafetySubTabView
      unit={unit}
      fields={emergenzaFields}
      sections={emergenzaSections}
      title="Emergenza & Continuità"
      onEdit={() => setEmergenzaModalOpen(true)}
    />
  );

  const sicurezzaTabContent = (
    <Section spacing="md">
      <ToggleGroup
        type="single"
        value={currentSafetyTab}
        onValueChange={(value) => {
          if (!value) return;
          handleSafetyTabSelect(value as SafetyTab);
        }}
        className="flex flex-wrap gap-2"
      >
        {allowedSafetyTabs.map((tabId) => (
          <ToggleGroupItem key={tabId} value={tabId}>
            {SAFETY_TAB_LABELS[tabId]}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {currentSafetyTab === 'rilevazione'
        ? rilevazioneTabContent
        : currentSafetyTab === 'ventilazione'
          ? ventilazioneTabContent
          : currentSafetyTab === 'abbattimento'
            ? abbattimentoTabContent
            : emergenzaTabContent}
    </Section>
  );

  const operativitaTabContent = (
    <Section spacing="lg">
      <Section>
        <SectionHeader title="Catena di sicurezza" />
        <SafetyChainSummary unit={unit} actionLabelMap={thresholdActionLabelMap} />
      </Section>

      <Section>
        <SectionHeader title="Sistemi di sicurezza" />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {safetyOverviewItems.map((item) => (
            <SafetyOverviewCard
              key={item.id}
              title={item.label}
              summary={item.summary}
              isActive={false}
              onClick={() => {
                handleTabSelect('sicurezza');
                handleSafetyTabSelect(item.id);
              }}
            />
          ))}
        </div>
      </Section>
    </Section>
  );

  const tabContent =
    currentTab === 'anagrafica'
      ? anagraficaTabContent
      : currentTab === 'componenti'
        ? componentiTabContent
        : currentTab === 'sicurezza'
          ? sicurezzaTabContent
          : operativitaTabContent;

  return (
    <PageLayout>
      <PageHeader title={unit.name} icon="ac_unit" />

      <TabNavigation
        activeTab={currentTab}
        onSelect={(tabId) => handleTabSelect(tabId as UnitTab)}
        tabs={[
          { id: 'operativita', label: 'Operatività' },
          { id: 'anagrafica', label: 'Anagrafica' },
          { id: 'componenti', label: 'Componenti' },
          { id: 'sicurezza', label: 'Sicurezza' },
        ]}
      />

      <PageContent>{tabContent}</PageContent>

      <RefrigerationUnitModal
        open={unitModalOpen}
        onOpenChange={setUnitModalOpen}
        mode="edit"
        refrigerationUnit={unit}
        contextLine={unitContextLine}
        onUpdate={async (input) => {
          await handleUnitUpdate(input);
          setUnitModalOpen(false);
        }}
        onDelete={handleUnitDelete}
        isSubmitting={updateRefrigerationUnit.isPending}
        isDeleting={deleteRefrigerationUnit.isPending}
      />

      <RefrigerationUnitSafetySectionModal
        open={detectionModalOpen}
        onOpenChange={setDetectionModalOpen}
        title="Modifica sistemi di rilevazione"
        defaultValues={{
          ...buildSafetyDefaults(),
          ...buildSafetyValues(unit),
        }}
        onSubmit={async (values) => {
          const { detectors } = safetyToInput(values);
          const detectionMaintenance = {
            name: normalizeText(values.detectionMaintenanceName),
            notes: normalizeText(values.detectionMaintenanceNotes),
          };
          await handleUnitUpdate({
            id: unit.id,
            technical: { safety: { detectors, detectionMaintenance } },
          });
        }}
        isSubmitting={updateRefrigerationUnit.isPending}
      >
        <SafetySubTabForm
          fields={rilevazioneScalarFields}
          sections={rilevazioneFormSections}
          disabled={updateRefrigerationUnit.isPending}
          customRenderers={{
            detectorPositions: () => <DetectorPositionsField />,
          }}
        />
      </RefrigerationUnitSafetySectionModal>

      <RefrigerationUnitSafetySectionModal
        open={ventilationModalOpen}
        onOpenChange={setVentilationModalOpen}
        title="Modifica ventilazione"
        defaultValues={{
          ...buildVentilationDefaults(),
          ...buildVentilationValues(unit),
        }}
        onSubmit={async (values) => {
          const { ventilation } = ventilationToInput(values);
          await handleUnitUpdate({
            id: unit.id,
            technical: { safety: { ventilation } },
          });
        }}
        isSubmitting={updateRefrigerationUnit.isPending}
      >
        <SafetySubTabForm
          fields={ventilazioneFields}
          sections={ventilazioneFormSections}
          disabled={updateRefrigerationUnit.isPending}
        />
      </RefrigerationUnitSafetySectionModal>

      <RefrigerationUnitSafetySectionModal
        open={abbattimentoModalOpen}
        onOpenChange={setAbbattimentoModalOpen}
        title="Modifica abbattimento & scarico"
        defaultValues={{
          ...buildAbbattimentoCombinedDefaults(),
          ...buildAbbattimentoCombinedValues(unit),
        }}
        onSubmit={async (values) => {
          const { scrubber, abatementSystem, vsRelief, abatementTank, vvfDocs, vvfDocsLocation } =
            abbattimentoCombinedToInput(values);
          await handleUnitUpdate({
            id: unit.id,
            technical: {
              safety: {
                scrubber,
                abatementSystem,
                vsRelief,
                abatementTank,
                vvfDocs,
                vvfDocsLocation,
              },
            },
          });
        }}
        isSubmitting={updateRefrigerationUnit.isPending}
      >
        <SafetySubTabForm
          fields={abbattimentoFields}
          sections={abbattimentoFormSections}
          disabled={updateRefrigerationUnit.isPending}
        />
      </RefrigerationUnitSafetySectionModal>

      <RefrigerationUnitSafetySectionModal
        open={emergenzaModalOpen}
        onOpenChange={setEmergenzaModalOpen}
        title="Modifica emergenza & continuità"
        defaultValues={{
          ...buildEmergenzaCombinedDefaults(),
          ...buildEmergenzaCombinedValues(unit),
        }}
        onSubmit={async (values) => {
          const { emergencySystems, powerBackup, valves } = emergenzaCombinedToInput(values);
          await handleUnitUpdate({
            id: unit.id,
            technical: { safety: { emergencySystems, powerBackup, valves } },
          });
        }}
        isSubmitting={updateRefrigerationUnit.isPending}
      >
        <SafetySubTabForm
          fields={emergenzaFields}
          sections={emergenzaFormSections}
          disabled={updateRefrigerationUnit.isPending}
        />
      </RefrigerationUnitSafetySectionModal>

      <ComponentModal
        state={componentModal}
        onOpenChange={(open) => {
          if (!open) setComponentModal(null);
        }}
        onSave={handleComponentSave}
        onDelete={componentModal?.mode === 'edit' ? handleComponentDelete : undefined}
        isSubmitting={updateRefrigerationUnit.isPending}
        isDeleting={updateRefrigerationUnit.isPending}
        componentOptions={componentCategoryOptions.map((option) => ({
          value: option.id,
          label: option.label,
        }))}
      />
    </PageLayout>
  );
}

interface SafetyOverviewCardProps {
  title: string;
  summary: string;
  isActive: boolean;
  onClick: () => void;
}

function SafetyOverviewCard({
  title,
  summary,
  isActive,
  onClick,
}: SafetyOverviewCardProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-lg border px-4 py-3 text-left shadow-sm transition-all duration-150 ${
        isActive
          ? 'border-brand-blue-500 bg-brand-blue-50'
          : 'border-[var(--border-default)] bg-surface'
      }`}
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary">{title}</p>
      <p className="text-sm font-medium text-text-primary">{summary}</p>
    </button>
  );
}
